import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Flex } from '@components/grid';
import { P } from '@components/typography/new';
import { Clock } from '@components/icons/clock';
import { useDaypartCountdown } from '@hooks/use-daypart-countdown';

/**
 * Formats a time string from HH:mm to readable format (e.g., "3 pm" or "3:30 pm")
 */
const formatDaypartTime = time => {
  if (!time) return '';
  const [hour, minute] = time.split(':').map(Number);
  const period = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour > 12 ? hour - 12 : hour || 12;
  return minute === 0 ? `${displayHour} ${period}` : `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

// Design tokens from Figma
const VARIANTS = {
  light: {
    availability: { color: '#277AAA' },
    countdown: { color: '#A51428' },
    starting: { color: '#277AAA' },
  },
  dark: {
    availability: { color: '#AAD1FF' },
    countdown: { color: '#F08273' },
    starting: { color: '#AAD1FF' },
  },
};

const Pill = styled(Flex)`
  border-radius: 8px;
  padding: 2px 6px;
  gap: 6px;
  align-items: center;
  width: fit-content;
`;

const StyledClock = styled(Clock)`
  flex-shrink: 0;
`;

/**
 * Reusable daypart warning/availability pill component
 * Matches Figma designs for menu categories, cart items, and product detail modals
 *
 * Now handles countdown logic internally - just pass daypart and timezone!
 *
 * @param {Object} daypart - Daypart object with startTime and endTime
 * @param {string} timezone - Restaurant timezone for countdown calculation
 * @param {string} categoryName - Name of the daypart category (for cart countdown text)
 */
const DaypartWarning = ({ daypart, timezone, categoryName, onDarkBg, ...rest }) => {
  const { minutesRemaining, secondsRemaining, isActive, isStarting } = useDaypartCountdown(daypart, timezone);

  // If no daypart, don't render
  if (!daypart) return null;

  // Determine variant automatically based on countdown state
  const getVariant = () => {
    if (!isActive) return 'availability';
    if (minutesRemaining !== null && minutesRemaining <= 30) {
      return isStarting ? 'starting' : 'countdown';
    }
    return 'availability';
  };

  const variant = getVariant();
  const theme = onDarkBg ? 'dark' : 'light';
  const variantConfig = VARIANTS[theme]?.[variant] || VARIANTS.light.availability;

  const getText = () => {
    switch (variant) {
      case 'countdown': {
        if (minutesRemaining <= 3 && secondsRemaining != null) {
          const mins = Math.floor(secondsRemaining / 60);
          const secs = secondsRemaining % 60;
          return `Only available for the next ${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        }
        return `Only available for the next ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}`;
      }
      case 'starting':
        return `Starts in ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}`;
      case 'availability':
      default:
        if (!daypart?.startTime || !daypart?.endTime) return null;
        return `Only available between ${formatDaypartTime(daypart.startTime)} - ${formatDaypartTime(daypart.endTime)}`;
    }
  };

  const text = getText();
  if (!text) return null;

  return (
    <Pill $variant={variant} backgroundColor={onDarkBg ? 'grey80' : 'warmWhite3'} {...rest}>
      <StyledClock width="20px" height="20px" fill={variantConfig.color} />
      <P variant="small-semi-bold" color={variantConfig.color}>
        {text}
      </P>
    </Pill>
  );
};

DaypartWarning.propTypes = {
  daypart: PropTypes.shape({
    startTime: PropTypes.string,
    endTime: PropTypes.string,
  }),
  timezone: PropTypes.string,
  categoryName: PropTypes.string,
  onDarkBg: PropTypes.bool,
};

DaypartWarning.defaultProps = {
  daypart: null,
  timezone: null,
  categoryName: '',
  onDarkBg: false,
};

export { DaypartWarning, formatDaypartTime };
